import React, { useEffect, useState } from 'react'

import styled from 'styled-components'
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap'

import i18n from 'live/services/i18n'
import Select from 'live/components/forms/ui/select'
import LiveModal from 'live/components/ui/Modal'
import { fetchAssets } from '../../widgets/panels/api'
import { CSVStaticData, ExportSelectOption, FullStaticData } from './types'
import {
    RIG_API,
    WELL_API,
    fetchAllEntities,
    fetchSingleEntity,
    queryEntitiesByType
} from '../../widgets/static-data/api/api'
import {
    RawActivityEvent,
    RawInterventionEvent,
    RawPhaseEvent,
    RawRigTableEvent,
    RawRunEvent,
    RawWellEvent,
    RestrictedConnection,
    RestrictedPipeMovement,
    RestrictedRateOfPenetration
} from '../../widgets/static-data/utils/type'

const ExportStaticDataModal = ({
    show,
    setShow,
    onClose = () => {},
    onExport = () => {}
}: {
    show: boolean
    setShow: (value: boolean) => void
    onClose?: () => void
    onExport?: () => void
}): JSX.Element => {
    const [wellOptions, setWellOptions] = useState<ExportSelectOption[]>([])
    const [rigOptions, setRigOptions] = useState<ExportSelectOption[]>([])
    const [interventionOptions, setInterventionOptions] = useState<ExportSelectOption[]>([])

    const [selectedRig, setSelectedRig] = useState<ExportSelectOption>()
    const [selectedWell, setSelectedWell] = useState<ExportSelectOption>()
    const [selectedIntervention, setSelectedIntervention] = useState<ExportSelectOption>()

    const [filterIntervention, setFilterIntervention] = useState<boolean>(false)

    const [fullStaticData, setFullStaticData] = useState<FullStaticData>()

    useEffect(() => {
        fetchAssets('rig').then(r => {
            setRigOptions(
                r?.map(rig => {
                    const newRig: ExportSelectOption = {
                        label: rig.name,
                        value: rig.id,
                        assetId: rig.id
                    }
                    return newRig
                })
            )
        })
    }, [])

    useEffect(() => {
        if (!selectedRig) {
            return
        }
        queryEntitiesByType(RIG_API, { assetId: { operator: '=', value: selectedRig.value } }).then(data => {
            if (!data || data.length === 0) {
                return setSelectedRig(undefined)
            }
            const staticDataRigId = Number(data[0].id)
            fetchAllEntities(staticDataRigId, 0, 9999999999999).then((r: FullStaticData) => {
                setFullStaticData(r)
                setWellOptions(
                    r.wells.map(w => {
                        const newWell: ExportSelectOption = {
                            label: w.name,
                            value: w.id,
                            assetId: w.assetId,
                            uuid: w.uuid
                        }
                        return newWell
                    })
                )
                setSelectedWell(undefined)
                setSelectedIntervention(undefined)
                setInterventionOptions([])
            })
        })
    }, [selectedRig])

    useEffect(() => {
        if (!selectedWell) {
            return
        }
        const options: ExportSelectOption[] = []
        options.push({ label: 'All Inteventions', value: -1 })
        const newOptions = fullStaticData.interventions
            .filter(intervention => +intervention.well_id === +selectedWell?.value)
            .map(i => {
                let start: number | string = i.start
                if (start) {
                    start = longToParsedDateString(start)
                }
                let end: number | string = i.end
                if (end) {
                    end = longToParsedDateString(end)
                }

                const newInter: ExportSelectOption = {
                    label: `${i.name} (${start}-${end ?? 'open'})`,
                    value: i.id,
                    id: i.id,
                    uuid: i.uuid
                }

                return newInter
            })

        const fullOptions = options.concat(newOptions)
        setInterventionOptions(fullOptions)
        setSelectedIntervention(fullOptions[0])
    }, [selectedWell])

    const longToParsedDateString = (epoch: number): string => {
        const date = new Date(epoch)
        const addZeroIfNeed = num => String(num).padStart(2, '0')
        return `${date.getFullYear()}-${addZeroIfNeed(date.getMonth() + 1)}-${addZeroIfNeed(
            date.getDate()
        )} ${addZeroIfNeed(date.getHours())}:${addZeroIfNeed(date.getMinutes())}`
    }

    const toUseTzHour = (longDate: number): number => {
        const tz = +(new Date().getTimezoneOffset() / 60).toFixed(1)
        const quantity = tz * 60 * 60 * 1000
        return longDate + quantity * -1
    }

    const createCsvStaticDataRow = ({
        well,
        intervention,
        section = undefined,
        rig_table = undefined,
        run = undefined,
        tub = {
            tubular_number: undefined,
            tubular: undefined
        },
        cement = {
            cement_plug: undefined,
            cement_plug_number: undefined
        },
        restricted_rop = undefined,
        restricted_pipe_movement = undefined,
        restricted_connection = undefined
    }: {
        well: RawWellEvent
        intervention: RawInterventionEvent
        section?: RawPhaseEvent
        rig_table?: RawRigTableEvent
        run?: RawRunEvent
        tub?: {
            tubular_number?: number
            tubular?: RawActivityEvent
        }
        cement?: {
            cement_plug_uuid?: string
            cement_plug_number?: number
            cement_plug?: { startMD?: any; finalMD?: any }
        }
        restricted_rop?: RestrictedRateOfPenetration
        restricted_pipe_movement?: RestrictedPipeMovement
        restricted_connection?: RestrictedConnection
    }) => {
        const intervention_start_date = new Date(toUseTzHour(intervention.start))?.toISOString()?.split('T')
        const intervention_end_date = intervention?.end
            ? new Date(toUseTzHour(intervention.end))?.toISOString()?.split('T')
            : undefined

        const { tubular_number, tubular } = tub ?? { ...tub }
        const { cement_plug_uuid, cement_plug_number, cement_plug } = cement ?? { ...cement }

        const section_start_date = section?.start
            ? new Date(toUseTzHour(section.start))?.toISOString()?.split('T')
            : undefined
        const section_end_date = section?.end
            ? new Date(toUseTzHour(section.end))?.toISOString()?.split('T')
            : undefined

        const rig_table_start_date = rig_table?.start
            ? new Date(toUseTzHour(rig_table.start))?.toISOString()?.split('T')
            : undefined
        const rig_table_end_date = rig_table?.end
            ? new Date(toUseTzHour(rig_table.end))?.toISOString()?.split('T')
            : undefined

        const run_start_date = run?.start ? new Date(toUseTzHour(run.start))?.toISOString()?.split('T') : undefined
        const run_end_date = run?.end ? new Date(toUseTzHour(run.end))?.toISOString()?.split('T') : undefined

        const tubular_start_date = tubular?.start
            ? new Date(toUseTzHour(tubular.start))?.toISOString()?.split('T')
            : undefined
        const tubular_end_date = tubular?.end
            ? new Date(toUseTzHour(tubular.end))?.toISOString()?.split('T')
            : undefined

        const restricted_rop_start_date = restricted_rop?.start
            ? new Date(toUseTzHour(restricted_rop.start))?.toISOString()?.split('T')
            : undefined
        const restricted_rop_end_date = restricted_rop?.end
            ? new Date(toUseTzHour(restricted_rop.end))?.toISOString()?.split('T')
            : undefined

        const restricted_pipe_movement_start_date = restricted_pipe_movement?.start
            ? new Date(toUseTzHour(restricted_pipe_movement.start))?.toISOString()?.split('T')
            : undefined
        const restricted_pipe_movement_end_date = restricted_pipe_movement?.end
            ? new Date(toUseTzHour(restricted_pipe_movement.end))?.toISOString()?.split('T')
            : undefined

        const restricted_connection_start_date = restricted_connection?.start
            ? new Date(toUseTzHour(restricted_connection.start))?.toISOString()?.split('T')
            : undefined
        const restricted_connection_end_date = restricted_connection?.end
            ? new Date(toUseTzHour(restricted_connection.end))?.toISOString()?.split('T')
            : undefined

        const row: CSVStaticData = {
            RIG_NAME: selectedRig.label,

            WELL_UUID: well.uuid,
            WELL_NAME: well.name,
            WELL_FIELD: well.fieldName,
            WELL_TYPE: well.wellType,
            WELL_WATER_DEPTH: well.waterDepth?.value?.toString(),
            WELL_GEODETIC_REFERENCE_SYSTEM: well.referenceSystem,
            WELL_LATITUDE: well.latitude?.toString(),
            WELL_LONGITUDE: well.longitude?.toString(),
            WELL_CATEGORY: well.wellCategory,

            INTERVENTION_UUID: intervention.uuid,
            INTERVENTION_NAME: intervention.name,
            INTERVENTION_TACTIC: intervention.tatic,
            INTERVENTION_RESPONSIBLE: intervention.responsible,
            INTERVENTION_START_DATE: intervention_start_date?.length > 0 ? intervention_start_date[0] : '',
            INTERVENTION_START_HOUR: intervention_start_date?.length > 1 ? intervention_start_date[1] : '',
            INTERVENTION_END_DATE: intervention_end_date?.length > 0 ? intervention_end_date[0] : '',
            INTERVENTION_END_HOUR: intervention_end_date?.length > 1 ? intervention_end_date[1] : '',

            SECTION_UUID: section?.uuid,
            SECTION_NAME: section?.name,
            SECTION_OD: section?.oD?.value?.toString(),
            SECTION_START_MD: section?.startMD?.value?.toString(),
            SECTION_FINAL_MD: section?.finalMD?.value?.toString(),
            SECTION_START_DATE: section_start_date?.length > 0 ? section_start_date[0] : '',
            SECTION_START_HOUR: section_start_date?.length > 1 ? section_start_date[1] : '',
            SECTION_END_DATE: section_end_date?.length > 0 ? section_end_date[0] : '',
            SECTION_END_HOUR: section_end_date?.length > 1 ? section_end_date[1] : '',
            SECTION_NO_PREVIOUS_CASING: section?.noPreviousCasingValue ? 'YES' : 'NO',
            SECTION_PREV_CASING_OD: section?.prevCasingOD?.value?.toString(),
            SECTION_PREV_CASING_FINAL_MD: section?.prevCasingFinalMD?.value?.toString(),
            SECTION_TYPE: section?.type,
            SECTION_RISER: section?.riser,

            RIG_TABLE_UUID: rig_table?.uuid,
            RIG_TABLE_STATUS_TYPE: rig_table?.status,
            RIG_START_DATE: rig_table_start_date?.length > 0 ? rig_table_start_date[0] : '',
            RIG_START_HOUR: rig_table_start_date?.length > 1 ? rig_table_start_date[1] : '',
            RIG_END_DATE: rig_table_end_date?.length > 0 ? rig_table_end_date[0] : '',
            RIG_END_HOUR: rig_table_end_date?.length > 1 ? rig_table_end_date[1] : '',

            RUN_UUID: run?.uuid,
            RUN_NUMBER: run?.number?.toString(),
            RUN_START_DATE: run_start_date?.length > 0 ? run_start_date[0] : '',
            RUN_START_HOUR: run_start_date?.length > 1 ? run_start_date[1] : '',
            RUN_END_DATE: run_end_date?.length > 0 ? run_end_date[0] : '',
            RUN_END_HOUR: run_end_date?.length > 1 ? run_end_date[1] : '',
            RUN_TYPE: run?.type,
            RUN_EXTEND_LAST_TUBULAR: run?.extendLastTubular ? 'YES' : 'NO',
            RUN_TUBULARS_INTERVAL_PER_TIME: run?.tubularsIntervalPerTime ? 'YES' : 'NO',

            RUN_CEMENT_PLUG_UUID: cement_plug_uuid,
            RUN_CEMENT_PLUG_PRESENT: cement_plug ? 'YES' : 'NO',
            RUN_CEMENT_PLUG_NUMBER: cement_plug_number?.toString(),
            RUN_CEMENT_PLUG_START_MD: cement_plug?.startMD?.value?.toString(),
            RUN_CEMENT_PLUG_FINAL_MD: cement_plug?.finalMD?.value?.toString(),
            RUN_START_MD: run?.startMD?.value?.toString(),
            RUN_FINAL_MD: run?.finalMD?.value?.toString(),

            TUBULAR_UUID: tubular?.uuid,
            TUBULAR_NUMBER: tubular_number?.toString(),
            TUBULAR_TYPE: tubular?.tubularType,
            TUBULAR_CONNECTION_TYPE: tubular?.connectionType,
            TUBULAR_PERMANENT_TUBULAR_INSTALLATION: tubular?.permanentTubularInstallation ? 'YES' : 'NO',
            TUBULAR_OD: tubular?.oD?.value?.toString(),
            TUBULAR_START_MD: tubular?.startMD?.value?.toString(),
            TUBULAR_FINAL_MD: tubular?.finalMD?.value?.toString(),
            THIRD_PARTY_RESPONSIBLE: tubular?.thirdPartyResponsible,
            TUBULAR_MATERIAL_TYPE: tubular?.materialType,
            TUBULAR_START_DATE: tubular_start_date?.length > 0 ? tubular_start_date[0] : '',
            TUBULAR_START_HOUR: tubular_start_date?.length > 1 ? tubular_start_date[1] : '',
            TUBULAR_END_DATE: tubular_end_date?.length > 0 ? tubular_end_date[0] : '',
            TUBULAR_END_HOUR: tubular_end_date?.length > 1 ? tubular_end_date[1] : '',

            TUBULAR_RESTRICTED_ROP_UUID: restricted_rop?.uuid,
            TUBULAR_RESTRICTED_ROP_TYPE: restricted_rop?.restrictedRopType,
            TUBULAR_RESTRICTED_ROP_START_MD: restricted_rop?.startMD?.value?.toString(),
            TUBULAR_RESTRICTED_ROP_FINAL_MD: restricted_rop?.finalMD?.value?.toString(),
            TUBULAR_RESTRICTED_ROP_GOAL_ACRONYM: restricted_rop?.restrictedRopSpeedAcronym,
            TUBULAR_RESTRICTED_ROP_GOAL_UOM: restricted_rop?.restrictedRopSpeed?.name,
            TUBULAR_RESTRICTED_ROP_GOAL_VALUE: restricted_rop?.restrictedRopSpeed?.value?.toString(),
            TUBULAR_RESTRICTED_ROP_START_DATE:
                restricted_rop_start_date?.length > 0 ? restricted_rop_start_date[0] : '',
            TUBULAR_RESTRICTED_ROP_START_HOUR:
                restricted_rop_start_date?.length > 1 ? restricted_rop_start_date[1] : '',
            TUBULAR_RESTRICTED_ROP_END_DATE: restricted_rop_end_date?.length > 0 ? restricted_rop_end_date[0] : '',
            TUBULAR_RESTRICTED_ROP_END_HOUR: restricted_rop_end_date?.length > 1 ? restricted_rop_end_date[1] : '',

            TUBULAR_RESTRICTED_PIPE_MOVEMENT_UUID: restricted_pipe_movement?.uuid,
            TUBULAR_RESTRICTED_PIPE_MOVEMENT_TYPE: restricted_pipe_movement?.restrictedPipeMovementType,
            TUBULAR_RESTRICTED_PIPE_MOVEMENT_GOAL_ACRONYM: restricted_pipe_movement?.restrictedPipeMovementSpeedAcronym,
            TUBULAR_RESTRICTED_PIPE_MOVEMENT_GOAL_UOM: restricted_pipe_movement?.restrictedPipeMovementSpeed?.name,
            TUBULAR_RESTRICTED_PIPE_MOVEMENT_GOAL_VALUE: restricted_pipe_movement?.restrictedPipeMovementSpeed?.value?.toString(),
            TUBULAR_RESTRICTED_PIPE_MOVEMENT_START_MD: restricted_pipe_movement?.startMD?.value?.toString(),
            TUBULAR_RESTRICTED_PIPE_MOVEMENT_FINAL_MD: restricted_pipe_movement?.finalMD?.value?.toString(),
            TUBULAR_RESTRICTED_PIPE_MOVEMENT_START_DATE:
                restricted_pipe_movement_start_date?.length > 0 ? restricted_pipe_movement_start_date[0] : '',
            TUBULAR_RESTRICTED_PIPE_MOVEMENT_START_HOUR:
                restricted_pipe_movement_start_date?.length > 1 ? restricted_pipe_movement_start_date[1] : '',
            TUBULAR_RESTRICTED_PIPE_MOVEMENT_END_DATE:
                restricted_pipe_movement_end_date?.length > 0 ? restricted_pipe_movement_end_date[0] : '',
            TUBULAR_RESTRICTED_PIPE_MOVEMENT_END_HOUR:
                restricted_pipe_movement_end_date?.length > 1 ? restricted_pipe_movement_end_date[1] : '',

            TUBULAR_RESTRICTED_CONNECTION_UUID: restricted_connection?.uuid,
            TUBULAR_RESTRICTED_CONNECTION_TYPE: restricted_connection?.restrictionType,
            TUBULAR_RESTRICTED_CONNECTION_GOAL_ACRONYM: restricted_connection?.restrictedTimeConnectionAcronym,
            TUBULAR_RESTRICTED_CONNECTION_GOAL_UOM: restricted_connection?.restrictedTimeConnection?.name,
            TUBULAR_RESTRICTED_CONNECTION_GOAL_VALUE: restricted_connection?.restrictedTimeConnection?.value?.toString(),
            TUBULAR_RESTRICTED_CONNECTION_START_MD: restricted_connection?.startMD?.value?.toString(),
            TUBULAR_RESTRICTED_CONNECTION_FINAL_MD: restricted_connection?.finalMD?.value?.toString(),
            TUBULAR_RESTRICTED_CONNECTION_START_DATE:
                restricted_connection_start_date?.length > 0 ? restricted_connection_start_date[0] : '',
            TUBULAR_RESTRICTED_CONNECTION_START_HOUR:
                restricted_connection_start_date?.length > 1 ? restricted_connection_start_date[1] : '',
            TUBULAR_RESTRICTED_CONNECTION_END_DATE:
                restricted_connection_end_date?.length > 0 ? restricted_connection_end_date[0] : '',
            TUBULAR_RESTRICTED_CONNECTION_END_HOUR:
                restricted_connection_end_date?.length > 1 ? restricted_connection_end_date[1] : ''
        }
        return row
    }

    const downloadAsCsv = () => {
        const rows: CSVStaticData[] = []

        const exportWell = fullStaticData.wells.find(w => selectedWell.assetId == w.assetId)
        const inters = selectedIntervention?.id
            ? fullStaticData.interventions.filter(int => selectedIntervention.uuid == int.uuid).sort((a,b) => (a.start - b.start))
            : fullStaticData.interventions.filter(int => exportWell.id == int.well_id).sort((a,b) => (a.start - b.start))

        for (const i of inters) {

            const rigTables = fullStaticData.rigTables.filter(rigtable => rigtable.intervention_id == i.id).sort((a,b) => (a.start - b.start))

            const sections = fullStaticData.sections.filter(sec => sec.intervention_id == i.id).sort((a,b) => (a.start - b.start))
            if (sections?.length > 0) {
                populateSections({ rig_tables: rigTables, intervention: i, sections: sections, rows: rows, well: exportWell })
            } else {
                rows.push(createCsvStaticDataRow({ intervention: i, well: exportWell, rig_table: rigTables[0] }))
            }
        }

        let csvContent = 'data:text/csv;charset=utf-8,'

        const header = Object.keys(rows[0])
        const contentRows = rows.map(row => header.map(key => row[key]).join(';'))

        csvContent += header.join(';') + '\r\n'
        contentRows.forEach(row => {
            csvContent += row + '\r\n'
        })

        var encodedUri = encodeURI(csvContent)
        var link = document.createElement('a')
        link.setAttribute('href', encodedUri)
        const date = new Date().toISOString()
        link.setAttribute('download', `${date}_interventions.csv`)
        document.body.appendChild(link)

        link.click()
    }

    const populateSections = ({
        rig_tables,
        intervention,
        sections,
        rows,
        well
    }: {
        well: RawWellEvent
        intervention: RawInterventionEvent
        sections: RawPhaseEvent[]
        rig_tables: RawRigTableEvent[]
        rows: CSVStaticData[]
    }) => {
        for (const section of sections) {
            const runs = fullStaticData.runs.filter(run => run.section_id == section.id).sort((a,b) => (a.start - b.start))
            if (runs?.length > 0) {
                populateRuns({
                    intervention: intervention,
                    rig_tables: rig_tables,
                    rows: rows,
                    runs: runs,
                    section: section,
                    well: well
                })
            } else {
                const rigTableForSection = rig_tables.filter(rig_table => rig_table.end === section.end)
                if (rigTableForSection.length > 0) {
                    for (const rig_table of rigTableForSection) {
                        rows.push(
                            createCsvStaticDataRow({
                                well: well,
                                intervention: intervention,
                                section: section,
                                rig_table: rig_table
                            })
                        )
                    }
                } else {
                    // If no rig table matches, still push a row with undefined rig_table
                    rows.push(
                        createCsvStaticDataRow({
                            well: well,
                            intervention: intervention,
                            section: section,
                            rig_table: undefined
                        })
                    )
                }
            }
        }
    }

    const populateRuns = ({
        runs,
        rig_tables,
        intervention,
        section,
        rows,
        well
    }: {
        runs: RawRunEvent[]
        well: RawWellEvent
        intervention: RawInterventionEvent
        section: RawPhaseEvent
        rig_tables: RawRigTableEvent[]
        rows: CSVStaticData[]
    }) => {
        for (const r of runs) {
            const tubulars = fullStaticData.tubulars.filter(tub => tub.run_id == r.id).sort((a,b) =>
            {
                if(a.start && b.start)
                {
                    return (a.start - b.start)
                }else{
                    if(a.startMD && a.startMD.value && b.startMD && b.startMD.value){
                       return (a.startMD.value - b.startMD.value)
                    }
                }
                return true
            })

            const rigTableForRun = rig_tables.filter(rig_table => {
                return (rig_table.end == r.end || rig_table.end == section.end || rig_table.start == r.start || rig_table.start == section.start)
            })

            if (tubulars?.length > 0) {
                for (const rig_table of rigTableForRun.length > 0 ? rigTableForRun : [undefined]) {
                    populateTubulars({
                        rows: rows,
                        well: well,
                        intervention: intervention,
                        rig_table: rig_table,
                        run: r,
                        section: section,
                        tubulars: tubulars
                    })
                }
            } else {
                if (r.cementedPlugs?.length > 0) {
                    for (const rig_table of rigTableForRun.length > 0 ? rigTableForRun : [undefined]) {
                        r.cementedPlugs.forEach((plg, idx) => {
                            rows.push(
                                createCsvStaticDataRow({
                                    well: well,
                                    intervention: intervention,
                                    cement: { cement_plug: plg, cement_plug_number: idx },
                                    section: section,
                                    run: r,
                                    rig_table: rig_table,
                                    restricted_rop: undefined,
                                    restricted_pipe_movement: undefined,
                                    restricted_connection: undefined
                                })
                            )
                        })
                    }
                } else {
                    for (const rig_table of rigTableForRun.length > 0 ? rigTableForRun : [undefined]) {
                        rows.push(
                            createCsvStaticDataRow({
                                well: well,
                                intervention: intervention,
                                cement: { cement_plug: undefined, cement_plug_number: undefined },
                                section: section,
                                run: r,
                                rig_table: rig_table,
                                restricted_rop: undefined,
                                restricted_pipe_movement: undefined,
                                restricted_connection: undefined
                            })
                        )
                    }
                }
            }
        }
    }

    const populateTubulars = ({
        tubulars,
        well,
        intervention,
        section,
        run,
        rig_table,
        rows
    }: {
        tubulars: RawActivityEvent[]
        well: RawWellEvent
        intervention: RawInterventionEvent
        section: RawPhaseEvent
        rig_table: RawRigTableEvent | undefined
        run: RawRunEvent
        rows: CSVStaticData[]
    }) => {
        let tubular_number = 0
        for (const t of tubulars) {
            tubular_number++

            if (
                t.restrictedConnections.length == 0 &&
                t.restrictedRops.length == 0 &&
                t.restrictedPipeMovements.length == 0
            ) {
                if (run.cementedPlugs?.length > 0) {
                    run.cementedPlugs.forEach((plg, idx) => {
                        rows.push(
                            createCsvStaticDataRow({
                                well: well,
                                intervention: intervention,
                                cement: { cement_plug: plg, cement_plug_number: idx },
                                tub: { tubular: t, tubular_number: tubular_number },
                                section: section,
                                run: run,
                                rig_table: rig_table,
                                restricted_rop: undefined,
                                restricted_pipe_movement: undefined,
                                restricted_connection: undefined
                            })
                        )
                    })
                } else {
                    rows.push(
                        createCsvStaticDataRow({
                            well: well,
                            intervention: intervention,
                            cement: { cement_plug: undefined, cement_plug_number: undefined },
                            tub: { tubular: t, tubular_number: tubular_number },
                            section: section,
                            run: run,
                            rig_table: rig_table,
                            restricted_rop: undefined,
                            restricted_pipe_movement: undefined,
                            restricted_connection: undefined
                        })
                    )
                }
            } else {
                t.restrictedRops.forEach(rrop => {
                    if (run.cementedPlugs?.length > 0) {
                        run.cementedPlugs.forEach((plg, idx) => {
                            rows.push(
                                createCsvStaticDataRow({
                                    well: well,
                                    intervention: intervention,
                                    cement: { cement_plug: plg, cement_plug_number: idx },
                                    tub: { tubular: t, tubular_number: tubular_number },
                                    section: section,
                                    run: run,
                                    rig_table: rig_table,
                                    restricted_rop: rrop,
                                    restricted_pipe_movement: undefined,
                                    restricted_connection: undefined
                                })
                            )
                        })
                    } else {
                        rows.push(
                            createCsvStaticDataRow({
                                well: well,
                                intervention: intervention,
                                cement: { cement_plug: undefined, cement_plug_number: undefined },
                                tub: { tubular: t, tubular_number: tubular_number },
                                section: section,
                                run: run,
                                rig_table: rig_table,
                                restricted_rop: rrop,
                                restricted_pipe_movement: undefined,
                                restricted_connection: undefined
                            })
                        )
                    }
                })

                t.restrictedPipeMovements.forEach(rmov => {
                    if (run.cementedPlugs?.length > 0) {
                        run.cementedPlugs.forEach((plg, idx) => {
                            rows.push(
                                createCsvStaticDataRow({
                                    well: well,
                                    intervention: intervention,
                                    cement: { cement_plug: plg, cement_plug_number: idx },
                                    tub: { tubular: t, tubular_number: tubular_number },
                                    section: section,
                                    run: run,
                                    rig_table: rig_table,
                                    restricted_rop: undefined,
                                    restricted_pipe_movement: rmov,
                                    restricted_connection: undefined
                                })
                            )
                        })
                    } else {
                        rows.push(
                            createCsvStaticDataRow({
                                well: well,
                                intervention: intervention,
                                cement: { cement_plug: undefined, cement_plug_number: undefined },
                                tub: { tubular: t, tubular_number: tubular_number },
                                section: section,
                                run: run,
                                rig_table: rig_table,
                                restricted_rop: undefined,
                                restricted_pipe_movement: rmov,
                                restricted_connection: undefined
                            })
                        )
                    }
                })

                t.restrictedConnections.forEach(rconn => {
                    if (run.cementedPlugs?.length > 0) {
                        run.cementedPlugs.forEach((plg, idx) => {
                            rows.push(
                                createCsvStaticDataRow({
                                    well: well,
                                    intervention: intervention,
                                    cement: { cement_plug: plg, cement_plug_number: idx },
                                    tub: { tubular: t, tubular_number: tubular_number },
                                    section: section,
                                    run: run,
                                    rig_table: rig_table,
                                    restricted_rop: undefined,
                                    restricted_pipe_movement: undefined,
                                    restricted_connection: rconn
                                })
                            )
                        })
                    } else {
                        rows.push(
                            createCsvStaticDataRow({
                                well: well,
                                intervention: intervention,
                                cement: { cement_plug: undefined, cement_plug_number: undefined },
                                tub: { tubular: t, tubular_number: tubular_number },
                                section: section,
                                run: run,
                                rig_table: rig_table,
                                restricted_rop: undefined,
                                restricted_pipe_movement: undefined,
                                restricted_connection: rconn
                            })
                        )
                    }
                })
            }
        }
    }

    return (
        <>
            <LiveModalWrapper show={show}>
                <LiveModal.Header>
                    <LiveModal.Title>
                        <span>{i18n('Export Static Data')}</span>
                    </LiveModal.Title>
                    <i
                        className="far fa-times close-btn"
                        onClick={() => {
                            onClose()
                            setShow(false)
                        }}
                    />
                </LiveModal.Header>

                <LiveModal.Body>
                    <ExportBody>
                        <div className="box">
                            <div className="input-label">Rig</div>
                            <Select
                                className={'well-field'}
                                onChange={(value: string, selected: ExportSelectOption) => {
                                    setSelectedRig(selected)
                                }}
                                value={selectedRig?.value}
                                selected={selectedRig}
                                options={rigOptions}
                                placeholder={i18n('Select or type Rig name')}
                            />
                        </div>

                        <div className="box">
                            <div className="input-label">Well</div>
                            <Select
                                className={'well-field'}
                                onChange={(value: string, selected: ExportSelectOption) => {
                                    setSelectedWell(selected)
                                }}
                                value={selectedWell}
                                selected={selectedWell}
                                options={wellOptions}
                                placeholder={i18n('Select or type Well name')}
                                disabled={!selectedRig}
                            />
                        </div>
                        <div className="box">
                            <div className="input-label">Intervention</div>
                            <Select
                                className={'well-field'}
                                onChange={(value: string, selected: ExportSelectOption) => {
                                    setFilterIntervention(true)
                                    setSelectedIntervention(selected)
                                }}
                                value={selectedIntervention}
                                selected={selectedIntervention}
                                options={interventionOptions}
                                placeholder={i18n('Select an Intervention')}
                                disabled={!selectedWell}
                            />
                        </div>
                    </ExportBody>
                </LiveModal.Body>

                <LiveModal.Footer>
                    <ActionBtn
                        style={{
                            padding: '8px 24px',
                            fontSize: '15px',
                            border: 'none',
                            borderRadius: '3px'
                        }}
                        onClick={() => {
                            downloadAsCsv()
                            onExport()
                        }}
                        className="btn btn-primary"
                        disabled={!selectedRig || !selectedWell || !selectedIntervention}
                    >
                        <i className="fas fa-download"></i>
                        {i18n('Download Static Data')}
                    </ActionBtn>
                    <button
                        className="btn-cancel"
                        onClick={() => {
                            onClose()
                            setShow(false)
                        }}
                    >
                        {i18n('Close')}
                    </button>
                </LiveModal.Footer>
            </LiveModalWrapper>
        </>
    )
}

export default ExportStaticDataModal

const LiveModalWrapper = styled(LiveModal)`
    display: flex !important;
    .modal-dialog {
        margin: auto;
        max-width: 384px;
        height: 594px;
    }

    .close-btn {
        position: absolute;
        top: 16px;
        right: 25px;
        z-index: 9999;

        cursor: pointer;
    }

    .modal-body {
        display: flex;
        flex-direction: column;

        padding: 20px;

        .assets-options {
            display: flex;
            gap: 8px;

            .Select-control,
            .Select-menu-outer {
                width: 177px;
                height: 32px;

                .Select-menu {
                    border: 1px solid var(--gray-400);

                    // Changing scrollbar css
                    /* width */
                    ::-webkit-scrollbar {
                        width: 8px;
                    }

                    /* outside */
                    ::-webkit-scrollbar-track {
                        background: var(--gray-100);
                    }

                    /* Handle */
                    ::-webkit-scrollbar-thumb {
                        border-radius: 8px;
                        background: var(--gray-500);
                    }
                }
            }
        }

        .start-date,
        .end-date .date-span {
            font-size: 1.3rem;
            line-height: 1.625rem;
            font-family: SourceSansPro-Semibold, Arial, Helvetica Neue, Helvetica, sans-serif;
        }
    }

    .modal-footer {
        display: flex;
        align-items: center;

        .btn-cancel {
            border: 1px solid var(--gray-400);
            padding: 9px 24px;
            font-size: 15px;
            font-weight: 600;
            border-radius: 3px;
            background: transparent;

            &:hover {
                opacity: 0.8;
            }
        }
    }
`

export const ActionBtn = styled.button`
    font-size: 13px;
    font-weight: 600;
    padding: 4px 16px;

    &.btn {
        border-color: #eee;
        color: #37a3ef;

        &:not(.btn-primary):hover {
            background-color: #f1f1f1;
        }

        &.btn-primary:hover {
            background: ${({ theme }) => theme['blue-700']};
        }
    }

    &.btn.btn-primary {
        border-color: #008ac7;
        color: #fff;
    }
    &:focus {
        outline: none;
    }
`

const ExportBody = styled.div`
    padding: 0px;
    align-items: flex-start;
    gap: 24px;
    align-self: stretch;

    .box {
        align-items: flex-start;
        gap: 4px;
        align-self: stretch;
    }

    .input-label {
        color: var(--Palette-Light-Mode-gray-800, #364159);
        font-size: 13px;
        font-style: normal;
        font-weight: 600;
        line-height: 16px;
    }
`
